import type { QueryClient } from '@tanstack/react-query';
import type { ConversationId, Message, MessageId } from '@cyphex/types';
import { mapTimelineMessages, writeTimeline } from './timelinePages';

/** Page 0 always holds the newest window — live messages land there. */
export function appendMessage(
  client: QueryClient,
  conversationId: ConversationId,
  message: Message,
  clientMsgId: string | null,
): void {
  writeTimeline(client, conversationId, (data) => {
    const matches = (existing: Message): boolean =>
      existing.id === message.id || (clientMsgId !== null && existing.id === clientMsgId);
    const known = data.pages.some((page) => page.messages.some(matches));
    if (known) return mapTimelineMessages(data, (existing) => (matches(existing) ? message : existing));

    const [newest, ...rest] = data.pages;
    if (!newest) return data;
    return {
      ...data,
      pages: [
        { ...newest, messages: [...newest.messages, message], lastSeq: Math.max(newest.lastSeq, message.seq) },
        ...rest,
      ],
    };
  });
}

/** Surgical patch — never refetches a page just to change one bubble. */
export function patchMessage(
  client: QueryClient,
  conversationId: ConversationId,
  messageId: MessageId,
  patch: (message: Message) => Message,
): void {
  writeTimeline(client, conversationId, (data) =>
    mapTimelineMessages(data, (message) => (message.id === messageId ? patch(message) : message)),
  );
}

/** Soft delete keeps the row so the timeline does not jump under the reader. */
export function tombstoneMessage(
  client: QueryClient,
  conversationId: ConversationId,
  messageId: MessageId,
  at: string,
): void {
  patchMessage(client, conversationId, messageId, (message) => ({
    ...message,
    state: 'deleted',
    body: null,
    bodyText: null,
    attachments: [],
    previews: [],
    reactions: [],
    voice: null,
    deletedAt: at,
  }));
}

export function dropMessage(client: QueryClient, conversationId: ConversationId, messageId: MessageId): void {
  writeTimeline(client, conversationId, (data) => ({
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      messages: page.messages.filter((message) => message.id !== messageId),
    })),
  }));
}
